/**
 * EVO — tashkiliy ma'lumotnomalar (ombor, yuridik shaxs, ko'lam).
 *
 * Faqat O'QISH: bu yozuvlar seed va migratsiya orqali yaratiladi va kundalik
 * ishda o'zgarmaydi. Lekin RO'YXAT majburiy — har bir hujjat formasi
 * (buyurtma, kirim, transfer, sanoq) ombor tanlashni talab qiladi va ID ni
 * qo'lda kiritib bo'lmaydi.
 *
 * ============================================================================
 * KO'LAM O'LCHOVI FOYDALANUVCHIGA QARAB TANLANADI
 * ============================================================================
 * `/api/org/warehouses` ilgari HAR DOIM ombor ko'lami bo'yicha filtrlardi va
 * filial ko'lamidagi foydalanuvchi (sotuvchi) `denyAll` ga tushib BO'SH massiv
 * olardi — buyurtma yaratish esa `warehouseId` ni majburiy qiladi. Endi server
 * `InventoryService.warehouseScopeCols()` bilan bir xil mantiqni qo'llaydi:
 * omborlar ro'yxati bo'lsa ombor bo'yicha, aks holda FILIAL bo'yicha.
 *
 * Jonli tekshirilgan: admin/rahbar/sotuvchi → MAIN + QUARANTINE + TRANSIT,
 * omborchi → faqat MAIN.
 */
import { api } from './api';

export interface Warehouse {
  readonly id: number;
  readonly code: string;
  readonly name: string;
  /** `main` | `transit` | `quarantine` | … — `transit` yo'ldagi tovar ombori. */
  readonly whType: string;
  readonly branchId: number;
  /** Salbiy qoldiqqa ruxsat — chiqim ekranida ogohlantirish uchun. */
  readonly allowNegative: boolean;
}

export interface LegalEntity {
  readonly id: number;
  readonly code: string;
  readonly name: string;
  readonly taxId: string | null;
  readonly baseCurrency: string;
}

/** `GET /api/org/my-scope` — UI default tanlovlarni shundan oladi. */
export interface MyScope {
  readonly full: boolean;
  readonly warehouseIds: readonly number[];
  readonly roleCodes: readonly string[];
}

/**
 * Filial — `GET /api/org/branches`.
 *
 * Endpoint FAQAT autentifikatsiya talab qiladi va javob KO'LAM bilan
 * filtrlangan: filialga biriktirilgan xodim bitta qator oladi, to'liq
 * ko'lamli (`all`) foydalanuvchi esa hammasini. Ya'ni ro'yxatning
 * uzunligining o'zi «men filial almashtira olamanmi?» degan savolga
 * javob beradi va alohida ruxsat tekshiruvi kerak emas.
 */
export interface Branch {
  readonly id: number;
  readonly code: string;
  readonly name: string;
  readonly legalEntityId: number;
  readonly address?: string | null;
  readonly phone?: string | null;
}

export function fetchBranches(signal?: AbortSignal): Promise<readonly Branch[]> {
  return api.get<readonly Branch[]>('/org/branches', { ...(signal ? { signal } : {}) });
}

export const orgKeys = {
  all: ['org'] as const,
  branches: ['org', 'branches'] as const,
  warehouses: ['org', 'warehouses'] as const,
  legalEntities: ['org', 'legal-entities'] as const,
  myScope: ['org', 'my-scope'] as const,
  /** Boshqaruv daraxti — `GET /api/org/tree`, `settings.edit` bilan. */
  tree: ['org', 'tree'] as const,
};

export function fetchWarehouses(signal?: AbortSignal): Promise<readonly Warehouse[]> {
  return api.get<readonly Warehouse[]>('/org/warehouses', { ...(signal ? { signal } : {}) });
}

export function fetchLegalEntities(signal?: AbortSignal): Promise<readonly LegalEntity[]> {
  return api.get<readonly LegalEntity[]>('/org/legal-entities', { ...(signal ? { signal } : {}) });
}

export function fetchMyScope(signal?: AbortSignal): Promise<MyScope> {
  return api.get<MyScope>('/org/my-scope', { ...(signal ? { signal } : {}) });
}

// ===========================================================================
// TASHKILIY TUZILMANI BOSHQARISH — `settings.edit` (faqat admin)
// ===========================================================================
/*
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ NIMA UCHUN BU YERDA, `admin.ts` DA EMAS                                  │
 * │                                                                          │
 * │ Bir xil endpoint oilasi (`/org/…`) va bir xil tushunchalar: filial,      │
 * │ ombor, kassa. Ikki faylga bo'lish `Warehouse` ni ikki marta e'lon        │
 * │ qilishga majbur qilardi va ular birinchi o'zgarishda ajralib ketardi.    │
 * │                                                                          │
 * │ Fayl sarlavhasidagi «Faqat O'QISH» endi FAQAT yuqoridagi qismga          │
 * │ tegishli — u hujjat formalari uchun va ruxsatsiz ishlaydi. Quyidagilar   │
 * │ esa boshqaruv ekrani uchun va admin ruxsatini talab qiladi.              │
 * └──────────────────────────────────────────────────────────────────────────┘
 */

/** Daraxtdagi ombor. `stockPositions` — arxivlash tugmasini oldindan o'chiradi. */
export interface OrgTreeWarehouse {
  readonly id: number;
  readonly code: string;
  readonly name: string;
  readonly whType: string;
  readonly branchId: number;
  readonly address: string | null;
  readonly allowNegative: boolean;
  readonly isActive: boolean;
  /** Noldan farq qiladigan qoldiq qatorlari soni. */
  readonly stockPositions: number;
}

export interface OrgTreeCashAccount {
  readonly id: number;
  readonly code: string;
  readonly name: string;
  readonly accountType: string;
  readonly currencyCode: string;
  readonly legalEntityId: number;
  readonly isActive: boolean;
  /** FAQAT `cashaccount.view` bilan keladi. Pul — SATR, hech qachon `number`. */
  readonly currentBalance?: string;
}

export interface OrgTreeBranch {
  readonly id: number;
  readonly code: string;
  readonly name: string;
  readonly legalEntityId: number;
  readonly address: string | null;
  readonly phone: string | null;
  readonly isActive: boolean;
  /** Filialga biriktirilgan xodimlar soni — 0 bo'lsa filial bo'sh turadi. */
  readonly userCount: number;
  /** `stock.value.view` bo'lmasa `null` — ustun so'rovga umuman kirmaydi. */
  readonly stockValue: string | null;
  readonly warehouses: readonly OrgTreeWarehouse[];
  readonly cashAccounts: readonly OrgTreeCashAccount[];
}

export interface OrgTree {
  readonly branches: readonly OrgTreeBranch[];
  /** Hech bir filialga biriktirilmagan kassalar (markaziy, valyuta, eski). */
  readonly unassignedCashAccounts: readonly OrgTreeCashAccount[];
}

export function fetchOrgTree(signal?: AbortSignal): Promise<OrgTree> {
  return api.get<OrgTree>('/org/tree', { ...(signal ? { signal } : {}) });
}

/**
 * SEHRGAR javobi: filial + ombor + kassa VA keyingi qadam.
 *
 * `nextStep` SERVERDAN keladi, ekranda yozilmaydi — boshqa mijoz (mobil,
 * skript) ham filialni xodimsiz qoldirmasligi kerak.
 */
export interface BranchSetupResult {
  readonly branch: { readonly id: number; readonly code: string; readonly name: string };
  readonly warehouse: { readonly id: number; readonly code: string; readonly name: string };
  readonly cashAccount: {
    readonly id: number;
    readonly code: string;
    readonly name: string;
    readonly currencyCode: string;
  };
  readonly nextStep: {
    readonly code: string;
    readonly message: string;
    readonly endpoint: string;
    readonly scope: { readonly scopeType: string; readonly scopeId: number };
  };
}

export interface BranchSetupInput {
  readonly code: string;
  readonly name: string;
  readonly legalEntityId: number;
  readonly address?: string;
  readonly phone?: string;
  readonly warehouseCode?: string;
  readonly warehouseName?: string;
  readonly cashCode?: string;
  readonly cashName?: string;
}

/**
 * Filial + ombor + kassani BITTA amalda ochadi.
 *
 * Bo'sh satrlar YUBORILMAYDI: server ularni ixtiyoriy deb biladi va o'zi
 * standart kod/nom yasaydi (`SAMARQAND-OMBOR`). Bo'sh satr yuborilsa esa
 * u `min(2)` tekshiruvidan o'tmay 400 qaytarardi.
 */
export function setupBranch(input: BranchSetupInput): Promise<BranchSetupResult> {
  return api.post<BranchSetupResult>('/org/branches/setup', {
    code: input.code,
    name: input.name,
    legalEntityId: input.legalEntityId,
    ...(input.address ? { address: input.address } : {}),
    ...(input.phone ? { phone: input.phone } : {}),
    ...(input.warehouseCode ? { warehouseCode: input.warehouseCode } : {}),
    ...(input.warehouseName ? { warehouseName: input.warehouseName } : {}),
    ...(input.cashCode ? { cashCode: input.cashCode } : {}),
    ...(input.cashName ? { cashName: input.cashName } : {}),
  });
}

export interface BranchPatchInput {
  readonly name?: string;
  readonly address?: string | null;
  readonly phone?: string | null;
}

/** Kod TAHRIRLANMAYDI — u hujjatlarda chop etilgan (server ham rad etadi). */
export function updateBranch(id: number, patch: BranchPatchInput): Promise<{ readonly id: number }> {
  return api.put(`/org/branches/${id}`, patch);
}

/** Qoldig'i yoki xodimi bor filial arxivlanmaydi (409). O'CHIRISH yo'q. */
export function archiveBranch(id: number): Promise<{ readonly id: number }> {
  return api.post(`/org/branches/${id}/archive`);
}

export interface WarehouseCreateInput {
  readonly code: string;
  readonly name: string;
  readonly branchId: number;
  readonly address?: string;
  /** `main` standart. `quarantine` filialga BITTA; `transit` bu yerda yo'q. */
  readonly whType?: 'main' | 'quarantine';
}

/**
 * Yangi ombor.
 *
 * `allowNegative` bu yerda YO'Q va bo'lmaydi ham: server sxemasi noma'lum
 * kalitni 400 bilan rad etadi. Salbiy qoldiq tannarxni buzadi.
 *
 * `transit` ro'yxatda yo'q: u butun kompaniyaga bitta va allaqachon bor
 * (server ikkinchisini 409 bilan rad etadi) — tanlov ko'rsatib, keyin xato
 * berish foydalanuvchini aldash bo'lardi.
 */
export function createWarehouse(input: WarehouseCreateInput): Promise<{ readonly id: number }> {
  return api.post('/org/warehouses', {
    code: input.code,
    name: input.name,
    branchId: input.branchId,
    ...(input.address ? { address: input.address } : {}),
    ...(input.whType ? { whType: input.whType } : {}),
  });
}

export function updateWarehouse(
  id: number,
  patch: { readonly name?: string; readonly address?: string | null },
): Promise<{ readonly id: number }> {
  return api.put(`/org/warehouses/${id}`, patch);
}

/** Qoldig'i bor ombor arxivlanmaydi (409, «N pozitsiya»). */
export function archiveWarehouse(id: number): Promise<{ readonly id: number }> {
  return api.post(`/org/warehouses/${id}/archive`);
}

export interface OrgCashAccountInput {
  readonly code: string;
  readonly name: string;
  readonly legalEntityId: number;
  readonly currencyCode: string;
  readonly branchId: number | null;
}

/** Filial kassasi — har doim `cash` turi (bank hisobi moliya ekranida ochiladi). */
export function createOrgCashAccount(input: OrgCashAccountInput): Promise<{ readonly id: number }> {
  return api.post('/org/cash-accounts', {
    code: input.code,
    name: input.name,
    accountType: 'cash',
    legalEntityId: input.legalEntityId,
    currencyCode: input.currencyCode,
    branchId: input.branchId,
  });
}

/**
 * Kassani filialga biriktiradi (yoki bog'lanishni uzadi).
 *
 * ⚠️ Bog'lanish `cash_accounts` jadvalida EMAS — u yerda `branch_id` ustuni
 * yo'q (DATA_MODEL §11: kassa yuridik shaxsga bog'langan). Server uni
 * sozlamada yuritadi (`org-admin.service.ts` dagi ramka). Ekran uchun farq
 * ko'rinmaydi, lekin bilib qo'yish kerak: bu bog'lanish FK bilan
 * himoyalanmagan.
 */
export function setCashAccountBranch(
  id: number,
  branchId: number | null,
): Promise<{ readonly id: number }> {
  return api.put(`/org/cash-accounts/${id}`, { branchId });
}

/** Qoldig'i noldan farq qiladigan kassa arxivlanmaydi (409). */
export function archiveOrgCashAccount(id: number): Promise<{ readonly id: number }> {
  return api.post(`/org/cash-accounts/${id}/archive`);
}
